import { defineColumns } from './piColumns.js';
import { EMPTY, formatDate, formatDays, formatInteger, formatPercent, formatPeriod, isNumber } from './piFormat.js';
import { ROLE_CLASS_OPTIONS, SIZE_BAND_OPTIONS, optionLabel } from './piQuery.js';
import { companyRoute } from './piNavigation.js';
import { listOf, objectOf } from './piRequests.js';
import { countText, ratioText } from './piTable.js';

export const PATTERN_GROUPS = [
  { key: 'recurring', label: 'Uso recorrente', description: 'Uso em pelo menos 75% dos períodos da janela (3 de 4 semanas ou 2 de 2 meses).' },
  { key: 'sporadic', label: 'Uso esporádico', description: 'Uso em pelo menos 1 período da janela, abaixo do recorrente.' },
  { key: 'no_use', label: 'Sem uso no período', description: 'Liberada (ou sem gate) e nenhum uso na janela.' },
  { key: 'used', label: 'Usou no período', description: 'Funcionalidade eventual: pelo menos um uso nos 90 dias.' },
];

export const CADENCE_LABELS = {
  daily: 'Diária',
  weekly: 'Semanal',
  monthly: 'Mensal',
  eventual: 'Eventual',
};

export const ATTRIBUTE_LABELS = {
  sizeBand: 'Porte',
  plan: 'Tipo de plano',
  tenure: 'Antiguidade (meses desde a criação)',
  platform: 'Canal de uso',
  roles: 'Papéis que usam',
};

export const MATURITY_STATES = ['ok', 'insufficient', 'not_measured'];

export const ASSOCIATION_TEXT = 'Associação, não causa: os atributos descrevem quem está em cada grupo, não explicam o uso.';

export const PARTIAL_ACTIONS_TEXT = 'Uso de parte das ações: a funcionalidade tem 3 ou mais ações no catálogo e a empresa usou menos da metade delas na janela.';

export const PARTIAL_ACTIONS_NONE_TEXT = 'Uso de parte das ações: nenhuma empresa marcada nesta janela. A marca só se aplica a funcionalidade com 3 ou mais ações no catálogo, quando a empresa usou menos da metade delas.';

export const SMALL_GROUP_COMPANIES = 30;

export const PATTERNS_DISABLED_MESSAGE = 'Os padrões de uso ainda não estão disponíveis neste ambiente (404).';

const textOf = (value) => (value === null || value === undefined ? '' : String(value).trim());

const numberOrNull = (value) => (isNumber(value) ? value : null);

const percentText = (value) => (isNumber(value) ? formatPercent(value) : EMPTY);

const knownGroup = (key) => {
  for (let i = 0; i < PATTERN_GROUPS.length; i++) {
    if (PATTERN_GROUPS[i].key === key) return PATTERN_GROUPS[i];
  }
  return null;
};

export const groupLabel = (key, label) => {
  const given = textOf(label);
  if (given) return given;
  const known = knownGroup(textOf(key));
  return known ? known.label : 'Grupo ' + (textOf(key) || EMPTY);
};

export const cadenceLabel = (cadence) => {
  const key = textOf(cadence).toLowerCase();
  return Object.prototype.hasOwnProperty.call(CADENCE_LABELS, key) ? CADENCE_LABELS[key] : 'Não definida';
};

export const maturityInfo = (maturity) => {
  const source = objectOf(maturity);
  const state = MATURITY_STATES.indexOf(source.state) !== -1 ? source.state : 'ok';
  const reason = textOf(source.reason);
  const measuredFrom = formatDate(source.measuredFrom);
  const readyOn = formatDate(source.readyOn);
  let text = '';
  if (state === 'insufficient') {
    text = (readyOn !== EMPTY
      ? 'Dados insuficientes: a primeira janela completa fecha em ' + readyOn
      : 'Dados insuficientes: ainda não há uma janela completa medida') +
      (measuredFrom !== EMPTY ? ' (medição desde ' + measuredFrom + ')' : '') + '.';
  } else if (state === 'not_measured') {
    text = 'Sem medição: nenhum evento desta funcionalidade é medido ainda.';
  }
  return {
    state: state,
    ready: state === 'ok',
    text: text,
    reasonText: reason && state !== 'ok' ? 'Motivo: ' + reason : '',
  };
};

const periodsText = (periods, unit) => {
  if (!isNumber(periods) || periods < 1) return '';
  if (unit === 'week') return formatInteger(periods) + (periods === 1 ? ' semana fechada' : ' semanas fechadas') + ' (segunda a domingo; semana incompleta não conta)';
  if (unit === 'month') return formatInteger(periods) + (periods === 1 ? ' mês fechado' : ' meses fechados');
  return formatInteger(periods) + (periods === 1 ? ' período' : ' períodos');
};

export const windowText = (windowInfo, cadence) => {
  const source = objectOf(windowInfo);
  const range = formatPeriod(source.from, source.to);
  const recurrent = ['daily', 'weekly', 'monthly'].indexOf(textOf(cadence).toLowerCase()) !== -1;
  const detail = periodsText(source.periods, textOf(source.unit).toLowerCase()) ||
    (recurrent ? '' : 'só "usou" ou "não usou", sem grupos de recorrência');
  if (range === EMPTY) return detail ? 'Janela: ' + detail : '';
  return 'Janela ' + range + (detail ? ': ' + detail : '');
};

export const metricText = (metric) => (textOf(metric) === 'punch_coverage'
  ? 'Grupos pela fração dos colaboradores elegíveis que bateram ponto na janela de 28 dias: 80% ou mais, de 50% a 79% e abaixo de 50%.'
  : 'Grupos pela quantidade de períodos da janela em que a empresa usou a funcionalidade.');

export const populationText = (population) => {
  const source = objectOf(population);
  const companies = numberOrNull(source.companies);
  const excluded = numberOrNull(source.excluded);
  const parts = [];
  if (companies !== null) parts.push(formatInteger(companies) + (companies === 1 ? ' empresa analisada' : ' empresas analisadas'));
  if (excluded !== null && excluded > 0) {
    parts.push(formatInteger(excluded) + ' fora da análise (menos de 3 usuários elegíveis)');
  }
  return parts.join(' · ');
};

export const groupRows = (groups) => listOf(groups).map((group, index) => {
  const key = textOf(group.key) || String(index);
  const known = knownGroup(key);
  const partial = key === 'no_use' ? null : numberOrNull(group.partialActions);
  return {
    key: key,
    label: groupLabel(key, group.label),
    description: known ? known.description : '',
    companies: numberOrNull(group.companies),
    companiesText: countText(group.companies),
    share: numberOrNull(group.share),
    shareText: percentText(group.share),
    medianUserShare: numberOrNull(group.medianUserShare),
    medianUserShareText: percentText(group.medianUserShare),
    medianActiveDays: numberOrNull(group.medianActiveDays),
    medianActiveDaysText: isNumber(group.medianActiveDays) ? formatDays(group.medianActiveDays) : EMPTY,
    partialActions: partial,
    partialActionsText: partial === null ? EMPTY : formatInteger(partial) + (partial === 1 ? ' empresa' : ' empresas'),
  };
});

const smallGroup = (companies) => companies !== null && companies > 0 && companies < SMALL_GROUP_COMPANIES;

const columnsOf = (groups) => (groups.length
  ? groups.map((group) => {
    const sizeText = group.companies === null ? '' : formatInteger(group.companies) + (group.companies === 1 ? ' empresa' : ' empresas');
    const small = smallGroup(group.companies);
    return {
      key: group.key,
      label: group.label,
      companies: group.companies,
      small: small,
      sizeText: sizeText,
      noteText: small ? sizeText + ' · amostra pequena' : sizeText,
    };
  })
  : PATTERN_GROUPS.slice(0, 3).map((group) => ({ key: group.key, label: group.label, companies: null, small: false, sizeText: '', noteText: '' })));

export const smallSampleText = (columns) => {
  const small = listOf(columns).filter((column) => column.small);
  if (!small.length) return '';
  return 'Amostra pequena: ' + small.map((column) => column.label + ' (' + column.sizeText + ')').join(', ') +
    (small.length === 1 ? ' tem' : ' têm') + ' menos de ' + SMALL_GROUP_COMPANIES +
    ' empresas; os percentuais descrevem o grupo e não sustentam diferença entre grupos (o insight P2 exige ' + SMALL_GROUP_COMPANIES + ' empresas em cada grupo).';
};

const attributeValueLabel = (attribute, value, label) => {
  const given = textOf(label);
  if (given) return given;
  const raw = textOf(value);
  if (attribute === 'sizeBand' && /^[A-E]$/.test(raw)) return optionLabel(SIZE_BAND_OPTIONS, raw);
  return raw || 'Sem valor';
};

export const comparisonBlocks = (comparison, columns) => listOf(comparison).map((block, index) => {
  const attribute = textOf(block.attribute);
  return {
    key: attribute || String(index),
    label: textOf(block.label) || ATTRIBUTE_LABELS[attribute] || attribute || 'Atributo',
    rows: listOf(block.values).map((value, valueIndex) => {
      const byGroup = objectOf(value.byGroup);
      const values = {};
      columns.forEach((column) => {
        values[column.key] = numberOrNull(byGroup[column.key]);
      });
      return {
        key: textOf(value.value) || String(valueIndex),
        label: attributeValueLabel(attribute, value.value, value.label),
        values: values,
        cells: columns.map((column) => ({ key: column.key, text: percentText(byGroup[column.key]) })),
      };
    }),
  };
}).filter((block) => block.rows.length);

export const flowRows = (flows) => listOf(flows).map((flow, index) => {
  const ends = listOf(flow.endEvents).map(textOf).filter(Boolean);
  const start = textOf(flow.startLabel) || textOf(flow.startEvent) || 'Início';
  const end = textOf(flow.endLabel) || ends.join(' ou ') || 'Conclusão';
  return {
    key: textOf(flow.startEvent) || String(index),
    label: start + ' → ' + end,
    eventsText: (textOf(flow.startEvent) || EMPTY) + ' → ' + (ends.length ? ends.join(' ou ') : EMPTY),
    companiesWithStart: numberOrNull(flow.companiesWithStart),
    medianCompletion: numberOrNull(flow.medianCompletion),
    belowHalf: numberOrNull(flow.belowHalf),
    companiesText: countText(flow.companiesWithStart),
    medianText: percentText(flow.medianCompletion),
    belowHalfText: ratioText(numberOrNull(flow.belowHalf), numberOrNull(flow.companiesWithStart)) || countText(flow.belowHalf),
  };
});

export const roleCoverageRows = (rows) => listOf(rows).map((row, index) => {
  const role = textOf(row.roleClass);
  return {
    key: role || String(index),
    label: textOf(row.label) || (role ? optionLabel(ROLE_CLASS_OPTIONS, role) : 'Papel'),
    share: numberOrNull(row.share),
    withUsers: numberOrNull(row.withUsers),
    adopting: numberOrNull(row.adopting),
    expectedText: row.expected === true ? 'Previsto' : (row.expected === false ? 'Não previsto' : EMPTY),
    shareText: isNumber(row.share) ? percentText(row.share) : 'Nenhuma empresa adotante tem esse papel',
    baseText: isNumber(row.withUsers) && row.withUsers > 0
      ? 'entre ' + countText(row.withUsers) + ' empresas adotantes com o papel' + (isNumber(row.adopting) ? ' (de ' + countText(row.adopting) + ')' : '')
      : '',
  };
});

export const exampleBlocks = (examples, groups) => listOf(examples).map((block, index) => {
  const key = textOf(block.group) || String(index);
  const listed = listOf(groups).filter((group) => group.key === key)[0];
  return {
    key: key,
    label: listed ? listed.label : groupLabel(key, null),
    companies: listOf(block.companies).slice(0, 10).map((company, companyIndex) => {
      const sizeBand = textOf(company.sizeBand);
      return {
        key: textOf(company.shard) + ':' + (textOf(company.companyId) || String(companyIndex)),
        companyId: textOf(company.companyId),
        shardCode: textOf(company.shardCode) || textOf(company.shard),
        name: textOf(company.name) || textOf(company.companyId) || 'Empresa',
        sizeBandText: /^[A-E]$/.test(sizeBand) ? optionLabel(SIZE_BAND_OPTIONS, sizeBand) : sizeBand,
        route: companyRoute(company.companyId, company.shard),
      };
    }),
  };
}).filter((block) => block.companies.length);

export const patternsView = (data) => {
  const source = objectOf(data);
  const maturity = maturityInfo(source.maturity);
  const groups = groupRows(source.groups);
  const columns = columnsOf(groups);
  const hasPartialActions = groups.some((group) => group.partialActions !== null && group.partialActions > 0);
  return {
    featureType: source.featureType,
    name: textOf(source.name),
    cadence: textOf(source.cadence),
    cadenceText: 'Cadência ' + cadenceLabel(source.cadence).toLowerCase(),
    metric: textOf(source.metric) || 'periods',
    metricText: metricText(source.metric),
    windowText: windowText(source.window, source.cadence),
    maturity: maturity,
    populationText: populationText(source.population),
    groups: groups,
    columns: columns,
    hasPartialActions: hasPartialActions,
    partialActionsNote: !hasPartialActions && groups.some((group) => group.partialActions !== null) ? PARTIAL_ACTIONS_NONE_TEXT : '',
    smallSampleText: smallSampleText(columns),
    comparison: comparisonBlocks(source.comparison, columns),
    flows: flowRows(source.flows),
    roleCoverage: roleCoverageRows(source.roleCoverage),
    examples: exampleBlocks(source.examples, groups),
    limitations: listOf(source.limitations).map(textOf).filter(Boolean),
  };
};

export const patternsEmpty = (view) => !!view && view.maturity.ready && !view.groups.length;

export const patternsError = (error) => {
  if (!error || error.kind !== 'disabled') return error;
  return Object.assign({}, error, { message: PATTERNS_DISABLED_MESSAGE });
};

export const PATTERN_GROUP_COLUMNS = defineColumns([
  { key: 'label', label: 'Grupo', type: 'text', infoKey: 'features.patterns.group', width: 36 },
  { key: 'companies', label: 'Empresas', type: 'int', infoKey: 'features.patterns.companies' },
  { key: 'share', label: '% do total', type: 'percent', infoKey: 'features.patterns.share' },
  { key: 'medianUserShare', label: 'Mediana de usuários que usam ÷ elegíveis', type: 'percent', infoKey: 'features.patterns.medianUserShare' },
  { key: 'medianActiveDays', label: 'Mediana de dias com uso', type: 'decimal', infoKey: 'features.patterns.medianActiveDays' },
  { key: 'partialActions', label: 'Uso de parte das ações', type: 'int', infoKey: 'features.patterns.partialActions' },
]);

export const patternGroupColumns = (view) => (objectOf(view).hasPartialActions
  ? PATTERN_GROUP_COLUMNS
  : PATTERN_GROUP_COLUMNS.filter((column) => column.key !== 'partialActions'));

const groupShare = (key) => (row) => objectOf(objectOf(row).values)[key];

const groupShareColumn = (key, label) => ({ key: key, label: label, type: 'percent', infoKey: 'features.patterns.groupShare', value: groupShare(key) });

export const PATTERN_COMPARISON_COLUMNS = defineColumns([
  { key: 'attribute', label: 'Atributo', type: 'text', infoKey: 'features.patterns.attribute', screen: false },
  { key: 'label', label: 'Valor', type: 'text', infoKey: 'features.patterns.attributeValue' },
  groupShareColumn('recurring', 'Uso recorrente'),
  groupShareColumn('sporadic', 'Uso esporádico'),
  groupShareColumn('no_use', 'Sem uso no período'),
  groupShareColumn('used', 'Usou no período'),
  groupShareColumn('high', '80% ou mais dos colaboradores bateram ponto'),
  groupShareColumn('medium', 'De 50% a 79% dos colaboradores bateram ponto'),
  groupShareColumn('low', 'Menos de 50% dos colaboradores bateram ponto'),
]);

export const patternComparisonColumns = (view) => {
  const keys = listOf(objectOf(view).columns).map((column) => column.key);
  const known = PATTERN_COMPARISON_COLUMNS.map((column) => column.key);
  const groups = PATTERN_COMPARISON_COLUMNS.filter((column) => keys.indexOf(column.key) !== -1);
  const extra = listOf(objectOf(view).columns)
    .filter((column) => known.indexOf(column.key) === -1)
    .map((column) => defineColumns([groupShareColumn(column.key, column.label)])[0]);
  return PATTERN_COMPARISON_COLUMNS.slice(0, 2).concat(groups, extra);
};

export const comparisonRows = (blocks) => listOf(blocks).reduce((all, block) => all.concat(listOf(block.rows).map((row) => ({
  key: block.key + ':' + row.key,
  attribute: block.label,
  label: row.label,
  values: row.values,
}))), []);

export const PATTERN_FLOW_COLUMNS = defineColumns([
  { key: 'label', label: 'Fluxo', type: 'text', infoKey: 'features.patterns.flow', width: 40 },
  { key: 'eventsText', label: 'Eventos do fluxo', type: 'text', infoKey: 'features.patterns.flowEvents', screen: false, width: 50 },
  { key: 'companiesWithStart', label: 'Empresas com 5 ou mais inícios', type: 'int', infoKey: 'features.patterns.flowCompanies' },
  { key: 'medianCompletion', label: 'Mediana de conclusão ÷ início', type: 'percent', infoKey: 'features.patterns.flowMedian' },
  { key: 'belowHalf', label: 'Empresas abaixo da metade', type: 'int', infoKey: 'features.patterns.flowBelowHalf' },
]);

export const PATTERN_ROLE_COLUMNS = defineColumns([
  { key: 'label', label: 'Papel', type: 'text', infoKey: 'features.patterns.role' },
  { key: 'expectedText', label: 'No catálogo', type: 'text', infoKey: 'features.patterns.roleExpected' },
  { key: 'share', label: 'Usa, entre as adotantes que têm o papel', type: 'percent', infoKey: 'features.patterns.roleShare' },
  { key: 'withUsers', label: 'Empresas adotantes com o papel', type: 'int', infoKey: 'features.patterns.roleWithUsers', screen: false },
  { key: 'adopting', label: 'Empresas adotantes', type: 'int', infoKey: 'features.patterns.roleAdopting', screen: false },
]);

export const patternLines = (view) => {
  const source = objectOf(view);
  return [source.windowText, source.cadenceText, source.metricText, source.populationText].map(textOf).filter(Boolean);
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piPatternsModel.js